import React, { useState } from "react";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import { makeStyles } from "@material-ui/core/styles";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import useDashboard from "../../hooks/useDashboard";

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  card: {
    padding: theme.spacing(2),
    minHeight: 120,
    borderRadius: 12
  },
  bigNumber: {
    fontSize: 34,
    fontWeight: 700
  },
  section: {
    padding: theme.spacing(2),
    borderRadius: 12,
    minHeight: 280
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(2)
  },
  row: {
    marginBottom: theme.spacing(1.5)
  },
  muted: {
    color: "#777"
  }
}));

const getTotal = (items, status) => {
  const found = items.find(item => item.status === status);
  return Number(found?.total || 0);
};

const Dashboard = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState("all");
  const { data, loading } = useDashboard(period);

  const open = getTotal(data.ticketsByStatus, "open");
  const pending = getTotal(data.ticketsByStatus, "pending");
  const closed = getTotal(data.ticketsByStatus, "closed");

  const usersChart = data.ticketsByUser.map(item => ({
    name: item.name,
    total: Number(item.total || 0)
  }));

  const queuesChart = data.ticketsByQueue.map(item => ({
    name: item.queue?.name || "Sem fila",
    total: Number(item.total || 0)
  }));

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Typography variant="h4" className={classes.title}>
        Dashboard Gerencial
      </Typography>

      <Typography variant="body2" className={classes.muted} paragraph>
        Visão executiva, comercial e operacional da Super Power Brasil.
      </Typography>

      <ButtonGroup color="primary" style={{ marginBottom: 24 }}>
        <Button variant={period === "today" ? "contained" : "outlined"} onClick={() => setPeriod("today")}>
          Hoje
        </Button>
        <Button variant={period === "week" ? "contained" : "outlined"} onClick={() => setPeriod("week")}>
          Semana
        </Button>
        <Button variant={period === "month" ? "contained" : "outlined"} onClick={() => setPeriod("month")}>
          Mês
        </Button>
        <Button variant={period === "all" ? "contained" : "outlined"} onClick={() => setPeriod("all")}>
          Total
        </Button>
      </ButtonGroup>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.card}>
            <Typography variant="subtitle2" color="primary">
              Total de Atendimentos
            </Typography>
            <Typography className={classes.bigNumber}>
              {data.totalTickets}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.card}>
            <Typography variant="subtitle2" color="primary">
              Em Atendimento
            </Typography>
            <Typography className={classes.bigNumber}>
              {open}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.card}>
            <Typography variant="subtitle2" color="primary">
              Pendentes
            </Typography>
            <Typography className={classes.bigNumber}>
              {pending}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.card}>
            <Typography variant="subtitle2" color="primary">
              Finalizados
            </Typography>
            <Typography className={classes.bigNumber}>
              {closed}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.section}>
            <Typography variant="h6" className={classes.title}>
              Ranking por Colaborador
            </Typography>

            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={usersChart}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.section}>
            <Typography variant="h6" className={classes.title}>
              Atendimentos por Fila
            </Typography>

            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={queuesChart}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.section}>
            <Typography variant="h6" className={classes.title}>
              Resumo Comercial
            </Typography>

            {data.ticketsByUser.map(user => (
              <Grid container key={user.id} className={classes.row}>
                <Grid item xs={8}>
                  <Typography>{user.name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography align="right">
                    {Number(user.total || 0)} atendimentos
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
